import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LOGIN } from "./RoutesUrl";
import { PostDetailContext, NavigationContext, SplashContext } from "../App";
import PostDetailModal from "../components/common/PostDetailModal";
import { SplashApi } from "./API";
import {
  getUserTargetRoute,
  getStoredUserTargetRoute,
  ONBOARDING_SKIP_PATHS,
  PENDING_SCREENS,
} from "../utils/authRoutingUtils";
import { syncSplashData } from "../utils/splashAdsUtils";
import { disableBrowserScrollRestoration, scrollWindowInstantly } from "../utils/scrollUtils";

// The SPA manages scroll positions itself (Feed restores its own position,
// every other page opens at the top). Native restoration would re-apply stale
// offsets from other pages on back/forward navigation.
disableBrowserScrollRestoration();

export const CountContext = createContext();

const ProtectedRoute = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { postDetail, isOpen, postModalClose } = useContext(PostDetailContext);
  const { updateCurrentUrl } = useContext(NavigationContext);
  const { splashResult, setSplashResult, syncSplashResult } = useContext(SplashContext) || {};

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [count, setCount] = useState(null);
  const [isCheckingSpecialization, setIsCheckingSpecialization] = useState(true);

  const updateCount = (val) => setCount(val);

  const applyVerificationRouting = (json, currentPath) => {
    if (!json?.status || !json?.user) return;
    const target = getUserTargetRoute(json.user, currentPath);
    if (target && target.toLowerCase() !== (currentPath || "").toLowerCase()) {
      navigate(target, { replace: true });
    }
  };

  const checkVerificationStatus = async () => {
    try {
      const signupData = JSON.parse(localStorage.getItem("SignupData"));
      const token = signupData?.data?.token;
      if (!token) return;
      const response = await fetch(SplashApi, { headers: { Authorization: `Bearer ${token}` } });
      const json = await response.json();
      if (json?.status && json?.user) {
        if (typeof syncSplashResult === "function") {
          syncSplashResult(json);
        } else {
          syncSplashData(json, splashResult, setSplashResult);
        }
      }
      applyVerificationRouting(json, location.pathname);
    } catch (error) {
      console.error("Verification status check failed:", error);
    }
  };

  const checkUser = () => {
    const signupData = JSON.parse(localStorage.getItem("SignupData") || "{}");
    const splashSaved = JSON.parse(localStorage.getItem("SplashData") || "{}");
    const user = splashSaved?.user || signupData?.data || {};

    if (!user?.id && !signupData?.data?.id && !signupData?.data?.token) {
      setIsLoggedIn(false);
      setIsCheckingSpecialization(false);
      return navigate(LOGIN);
    }

    const currentPath = location.pathname;
    const target = getStoredUserTargetRoute(currentPath);

    if (target && target.toLowerCase() !== currentPath.toLowerCase()) {
      setIsLoggedIn(false);
      setIsCheckingSpecialization(false);
      return navigate(target, { replace: true });
    }

    setIsLoggedIn(true);
    setIsCheckingSpecialization(false);
  };

  // When navigating to any route other than /feed, reset window scroll to top
  // so pages do not inherit the previous page's scroll position. This runs as a
  // layout effect (before paint) and bypasses Bootstrap's global smooth
  // scroll-behavior, so the new page never flashes at the old offset and then
  // glides up. Feed restores its own position in its own layout effect.
  useLayoutEffect(() => {
    if (location.pathname !== "/feed" && location.pathname !== "/") {
      scrollWindowInstantly(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    updateCurrentUrl(location.pathname);
  }, [location.pathname, updateCurrentUrl]);

  useEffect(() => {
    document.body.style.backgroundColor = isLoggedIn ? "#f0f7fa" : "#1dc1dd";
    return () => { document.body.style.backgroundColor = "#1dc1dd"; };
  }, [isLoggedIn]);

  // Initial auth check and guard on route change
  useEffect(() => {
    checkUser();
  }, [location.pathname]);

  // On route change, fetch fresh verification status
  // Skip onboarding/waiting paths to prevent loops
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => { setIsMounted(true); }, []);

  useEffect(() => {
    if (!isLoggedIn || !isMounted) return;
    if (ONBOARDING_SKIP_PATHS.includes(location.pathname)) return;
    checkVerificationStatus();
  }, [isLoggedIn, location.pathname, isMounted]);

  // Poll verification status on pending screens so re-verified users get redirected
  useEffect(() => {
    if (!isLoggedIn) return;
    if (!PENDING_SCREENS.includes(location.pathname)) return;
    const poll = async () => {
      try {
        const signupData = JSON.parse(localStorage.getItem("SignupData"));
        const token = signupData?.data?.token;
        if (!token) return;
        const response = await fetch(SplashApi, { headers: { Authorization: `Bearer ${token}` } });
        const json = await response.json();
        if (!json?.status || !json?.user) return;
        if (typeof syncSplashResult === "function") {
          syncSplashResult(json);
        } else {
          syncSplashData(json, splashResult, setSplashResult);
        }
        if (json.user.verified === 0) return;
        applyVerificationRouting(json, location.pathname);
      } catch {}
    };
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, [isLoggedIn, location.pathname, syncSplashResult, splashResult, setSplashResult]);

  if (isCheckingSpecialization) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: "#f0f7fa" }} />
    );
  }

  return (
    <>
      {isLoggedIn ? (
        <CountContext.Provider value={{ count, updateCount }}>
          {isOpen && (
            <PostDetailModal isOpen={isOpen} onClose={postModalClose} post={postDetail} />
          )}
          {props.children}
        </CountContext.Provider>
      ) : null}
    </>
  );
};

export default ProtectedRoute;
